import api from '../axios.config';
import { API_ENDPOINTS } from '../endpoints';
import { ApiResponse } from 'types';

/**
 * What a plan says. Mirrors OperatorPlan on the backend.
 *
 * Every field here is a count somebody is about to act on, so none of them is
 * optional in the UI: a plan that shows what it would add without showing what
 * it would skip is how a list looks complete when it is not.
 */
export interface OperatorPlan {
  planId: string;
  /** SHA-256 of the RESOLVED plan. Null when the plan cannot be run. */
  planHash: string | null;
  intent: 'ADD_FILE_TO_CAMPAIGN' | 'UNRECOGNISED';
  summary: string;
  campaign: {
    id: string; name: string; status: string; standing: boolean;
    templateId: string; templateName: string; templateSlots: number;
  } | null;
  mapping: {
    phoneColumn: number; nameColumn: number;
    slotColumns: Record<string, number>; contactColumns: number[];
    headers: string[]; notes: string[];
  } | null;
  audience: {
    rowsInFile: number; rowsUnusable: number; distinctNumbers: number;
    alreadyPending: number; suppressedOrgWideDoNotContact: number;
    suppressionFullyChecked: boolean; wouldBeAdded: number; sample: string[];
  } | null;
  cost: {
    available: boolean; currency: string | null; ratePerUnit: number | null;
    total: number | null; messages: number; note: string | null;
  };
  irreversible: string[];
  questions: string[];
  blockers: string[];
  confirmRoles: string[];
  createdAt: string;
  /** Present when the request was a question rather than an action. */
  answer: string | null;
  /** The figures behind that answer, named — the part somebody quotes. */
  facts: { label: string; value: string }[];
}

class OperatorService {
  /**
   * Ask what an instruction would do. Nothing is sent, enrolled or changed.
   *
   * Multipart because the file is half the instruction: "add this file to the
   * Diwali campaign" cannot be planned without reading the sheet.
   */
  async plan(
    message: string,
    file?: File,
    /**
     * An interpretation worked out somewhere else — a model in this browser,
     * for a trial. It replaces the server's PARSER and nothing else: the
     * campaign, the counts and the permissions are still the server's.
     */
    supplied?: { intent: string; campaignHint: string | null },
  ): Promise<ApiResponse<OperatorPlan>> {
    try {
      const form = new FormData();
      form.append('message', message);
      if (file) form.append('file', file);
      if (supplied) {
        form.append('intent', supplied.intent);
        if (supplied.campaignHint) form.append('campaignHint', supplied.campaignHint);
      }
      const res = await api.post(`${API_ENDPOINTS.OPERATOR}/plan`, form);
      return { success: res.data.success, data: res.data.data, message: res.data.message };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Could not plan that' };
    }
  }
}

export const operatorService = new OperatorService();
